import React, { useEffect, useMemo } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SkeletonForm } from '@node-space/storybook-components'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { AddressFormFields } from 'components/AddressFormFields/AddressFormFields'
import { getAddressFields } from 'components/AddressFormFields/utils'
import { useCountriesContext } from 'contexts/countriesContext'
import { useFinpromContext } from 'hooks/context/useFinpromContext'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useBusinessAddressMutation } from 'hooks/mutations/useBusinessAddressMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { getIsFinpromCountry } from 'pages/Finprom/utils'
import { FormBody } from 'pages/Verification/_components/FormBody'
import { FormGetError } from 'pages/Verification/_components/FormGetError'
import { FormSubmitButton } from 'pages/Verification/_components/FormSubmitButton'
import { FormSubmitError } from 'pages/Verification/_components/FormSubmitError'
import { BaseErrorResponse } from 'types/errors'
import { BusinessAddressFormFields } from 'types/verification'
import { BusinessAddressFormData } from './helpers/types'
import { useBusinessAddressForm } from './helpers/useBusinessAddressForm'

export const BusinessAddressForm = () => {
  const { t } = useTranslation()
  const countries = useCountriesContext()
  const {
    isWatchingBusinessAddress,
    isEnabled: isFinpromEnabled,
    setShowBackToBusinessAddress,
    onLeaveBusinessAddress,
  } = useFinpromContext()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { businessAddress, setUnsavedChanges, submitStep } = useVerificationContext()
  const businessAddressData = (businessAddress?.data || {}) as BusinessAddressFormData

  const { mutate: putBusinessAddress, isPending: isLoadingSubmitRequest } =
    useBusinessAddressMutation()

  const { form, formValues, formFields, formErrors, isFormDirty, resetForm } =
    useBusinessAddressForm(countries?.filteredCountryOptions, businessAddressData)

  const addressFields = getAddressFields(formFields)

  const isLoading = useMemo(
    () => countries?.isLoading || businessAddress?.isFetching,
    [countries?.isLoading, businessAddress?.isFetching]
  )

  const isGetError = useMemo(
    () => countries?.isErrorLoading || businessAddress?.isError,
    [countries?.isErrorLoading, businessAddress?.isError]
  )

  const onSubmit: SubmitHandler<typeof formValues> = (formValues: BusinessAddressFormFields) => {
    if (!isFormDirty) {
      submitStep()
      return
    }
    resetRequestError()

    putBusinessAddress(formValues, {
      onSuccess: () => {
        if (isFinpromEnabled) {
          if (
            !isWatchingBusinessAddress &&
            businessAddressData?.countryCode !== formValues?.countryCode
          ) {
            setShowBackToBusinessAddress(true)
          }
          if (isWatchingBusinessAddress && getIsFinpromCountry(formValues?.countryCode)) {
            onLeaveBusinessAddress()
          }
          businessAddress?.refetch()
        }
        submitStep()
      },
      onError: (error: BaseErrorResponse) => {
        const sentryTraceId = logSentryErrorAndGetTraceId('putBusinessAddress', error)
        setRequestError({
          errorCode: error?.status,
          sentryTraceId,
          show: true,
        })
      },
    })
  }

  useEffect(() => {
    !businessAddress?.isFetching && resetForm()
  }, [businessAddress?.isFetching])

  useEffect(() => {
    setUnsavedChanges(isFormDirty)
  }, [isFormDirty])

  return (
    <>
      {isLoading ? (
        <SkeletonForm name="Business address" />
      ) : isGetError ? (
        <FormGetError />
      ) : (
        <form onSubmit={form?.handleSubmit(onSubmit)}>
          <FormBody>
            {requestError?.show && (
              <FormSubmitError
                errorCode={requestError?.errorCode}
                sentryTraceId={requestError?.sentryTraceId}
              />
            )}
            <AddressFormFields
              label={t('formFields.registeredBusinessAddress')}
              addressFields={addressFields}
              isLoadingCountries={countries?.isLoading}
            />
            <FormSubmitButton
              loading={form?.formState?.isSubmitting || isLoadingSubmitRequest}
              disabled={!!Object.keys(formErrors)?.length}
            />
          </FormBody>
        </form>
      )}
    </>
  )
}
